import type {
  DeviceAssignmentData,
  DeviceRegistrationData,
  DeviceUnassignData,
} from '@fleet/shared';
import { apiFetch } from './client';

/** Ein Rohsatz in der Liste. */
export interface RawFrameSummary {
  id: number;
  imei: string;
  receivedAt: string;
  source: string;
  codecId?: number;
  bytes: number;
  /** Fehlt, wenn der Frame nicht lesbar ist — dann steht `parseError`. */
  recordCount?: number;
  crcOk?: boolean;
  parseError?: string;
  processedAt?: string;
  anonymization: string;
}

export interface DecodedIoValue {
  id: number;
  label: string;
  value: number | string;
  unit?: string;
  /** Steht die ID im Katalog des Herstellers? */
  known: boolean;
  /** Gruppe aus der Herstellertabelle, z. B. „OBD OEM elements". */
  group?: string;
  /**
   * Bedingter Multiplikator im Klartext. Er wird bewusst **nicht** angewandt —
   * lieber ein Rohwert mit Hinweis als eine stillschweigend falsche Umrechnung.
   */
  multiplierNote?: string;
}

export interface DecodedRecordView {
  recordedAt: string;
  priority: number;
  longitude: number;
  latitude: number;
  altitudeM: number;
  angleDeg: number;
  satellites: number;
  speedKmh: number;
  hasFix: boolean;
  eventIoId: number;
  eventLabel?: string;
  io: DecodedIoValue[];
}

export interface RawFrameDetail extends RawFrameSummary {
  anonymizedAt?: string;
  anonymizationDetail?: unknown;
  /** Der Frame in Hexadezimal — beim Debuggen manchmal die einzige Wahrheit. */
  hex: string;
  records: DecodedRecordView[];
}

/** Fahrzeug, so weit die Geräteliste es braucht. */
export interface AssignedVehicle {
  id: string;
  licensePlate: string;
  displayName: string;
}

export interface DeviceAssignmentView {
  id: string;
  validFrom: string;
  /** Fehlt = aktuell montiert. */
  validTo?: string;
  note?: string;
  vehicle: AssignedVehicle;
}

export interface TelematicsDeviceView {
  id: string;
  imei: string;
  model: string;
  serialNumber?: string;
  firmware?: string;
  status: string;
  lastSeenAt?: string;
  /** Fehlt, wenn das Gerät gerade in keinem Fahrzeug steckt. */
  currentAssignment?: Omit<DeviceAssignmentView, 'validTo'>;
}

export const telematicsApi = {
  devices: () => apiFetch<TelematicsDeviceView[]>('/api/v1/telematics/devices'),

  register: (body: DeviceRegistrationData) =>
    apiFetch<{ id: string; imei: string; status: string }>('/api/v1/telematics/devices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  assignments: (deviceId: string) =>
    apiFetch<DeviceAssignmentView[]>(`/api/v1/telematics/devices/${deviceId}/assignments`),

  /** Einbauen und Umstecken sind derselbe Aufruf — siehe TelematicsController. */
  assign: (deviceId: string, body: DeviceAssignmentData) =>
    apiFetch(`/api/v1/telematics/devices/${deviceId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  endAssignment: (deviceId: string, body: DeviceUnassignData) =>
    apiFetch(`/api/v1/telematics/devices/${deviceId}/assignments/end`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  raw: (filter: { imei?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (filter.imei) params.set('imei', filter.imei);
    if (filter.limit) params.set('limit', String(filter.limit));
    return apiFetch<RawFrameSummary[]>(`/api/v1/telematics/raw?${params}`);
  },

  rawDetail: (id: number) => apiFetch<RawFrameDetail>(`/api/v1/telematics/raw/${id}`),
};
